// src/services/dateService.js
// Unified date formatting and parsing utilities
// Combines functionality from utils/dateUtils.js and ProductionToolkit/utils/dateUtils.js
// Supports both standard Date objects and Firestore Timestamps

import { Timestamp } from "firebase/firestore";

// ============================================================================
// PARSING FUNCTIONS
// ============================================================================

/**
 * Parse a date string in DD-MM-YYYY format to a Date object
 * @param {string} dateStr - Date string in DD-MM-YYYY format
 * @returns {Date|null} Parsed date or null if invalid
 */
export function parseDDMMYYYY(dateStr) {
    if (!dateStr || typeof dateStr !== 'string') return null;
    
    try {
        // Handle DD-MM-YYYY format
        const match = dateStr.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
        if (match) {
            const [, day, month, year] = match;
            // Note: JavaScript Date months are 0-indexed
            return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        }
        
        // Handle YYYY-MM-DD format
        const isoMatch = dateStr.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
        if (isoMatch) {
            const [, year, month, day] = isoMatch;
            return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        }
        
        // Try native Date parsing as fallback
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
            return date;
        }
        
        return null;
    } catch (error) {
        console.warn('[dateService] Failed to parse date:', dateStr, error);
        return null;
    }
}

// ============================================================================
// FORMATTING FUNCTIONS
// ============================================================================

/**
 * Format a Date object to DD-MM-YYYY string
 * @param {Date} date - Date object to format
 * @returns {string} Formatted date string or empty string if invalid
 */
export function formatDDMMYYYY(date) {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
        return '';
    }
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}-${month}-${year}`;
}

/**
 * Format a Date object to YYYY-MM-DD string (ISO format)
 * @param {Date} date - Date object to format
 * @returns {string} Formatted date string or empty string if invalid
 */
export function formatYYYYMMDD(date) {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
        return '';
    }
    
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    
    return `${year}-${month}-${day}`;
}

/**
 * Format a Date object to a readable string
 * @param {Date} date - Date object to format
 * @returns {string} Formatted readable date string
 */
export function formatReadableDate(date) {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
        return '';
    }
    
    return date.toLocaleDateString('en-GB', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
}

/**
 * Return today's date in "YYYY-MM-DD" format
 * @returns {string} Today's date in YYYY-MM-DD format
 */
export function today() {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
}

/** Next Friday (today if today is Friday), YYYY-MM-DD. Default delivery for standing orders. */
export function getUpcomingFriday() {
    const todayDate = new Date();
    const day = todayDate.getDay();
    const daysToAdd = (5 - day + 7) % 7;
    const friday = new Date(todayDate);
    friday.setDate(todayDate.getDate() + daysToAdd);
    return formatYYYYMMDD(friday);
}

// ============================================================================
// FIRESTORE TIMESTAMP FUNCTIONS
// ============================================================================

/**
 * Convert a "YYYY-MM-DD" string to a Firestore Timestamp at midnight UTC
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {Timestamp} Firestore Timestamp
 */
export function dateStringToTimestamp(dateStr) {
    const [year, month, day] = dateStr.split("-").map(Number);
    // JS Date: month is 0-indexed
    const d = new Date(Date.UTC(year, month - 1, day, 0, 0, 0));
    return Timestamp.fromDate(d);
}

/**
 * Convert a Firestore Timestamp to a "YYYY-MM-DD" string
 * If input is already a string or Date, handles accordingly
 * @param {Timestamp|Date|string} ts - Timestamp, Date, or string to convert
 * @returns {string} Date string in YYYY-MM-DD format
 */
export function timestampToDateString(ts) {
    let d;
    if (ts instanceof Timestamp) {
        d = ts.toDate();
    } else if (ts instanceof Date) {
        d = ts;
    } else if (typeof ts === "string") {
        return ts;
    } else {
        return "";
    }
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
}

/**
 * Format a Firestore Timestamp (or Date) into a localized date string
 * @param {Timestamp|Date|string} ts - Timestamp, Date, or string to format
 * @param {string} locale - Locale code (e.g., "en-GB")
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted localized date string
 */
export function timestampToLocaleString(
    ts,
    locale = undefined,
    options = { year: "numeric", month: "short", day: "numeric" }
) {
    let d;
    if (ts instanceof Timestamp) {
        d = ts.toDate();
    } else if (ts instanceof Date) {
        d = ts;
    } else if (typeof ts === "string") {
        d = new Date(ts);
    } else {
        return "";
    }
    return new Intl.DateTimeFormat(locale, options).format(d);
}

/**
 * Coerce Firestore Timestamp, ISO string, or Date-like value to Date.
 * @param {*} value
 * @returns {Date|null}
 */
export function coerceToDate(value) {
    if (value == null || value === '') return null;
    if (value instanceof Timestamp) {
        const d = value.toDate();
        return d instanceof Date && !isNaN(d.getTime()) ? d : null;
    }
    if (value instanceof Date) {
        return isNaN(value.getTime()) ? null : value;
    }
    if (typeof value === 'object' && typeof value.toDate === 'function') {
        const d = value.toDate();
        return d instanceof Date && !isNaN(d.getTime()) ? d : null;
    }
    if (typeof value === 'object' && typeof value.seconds === 'number') {
        const d = new Date(value.seconds * 1000);
        return isNaN(d.getTime()) ? null : d;
    }
    if (typeof value === 'string' || typeof value === 'number') {
        const d = new Date(value);
        return isNaN(d.getTime()) ? null : d;
    }
    return null;
}

/**
 * Format any date-like value for OrderDesk checkout_data.Delivery / Dispatch (DD-MM-YYYY).
 * @param {*} value - ISO string, DD-MM-YYYY string, Date, Firestore Timestamp, etc.
 * @returns {string}
 */
export function toOrderDeskCheckoutDate(value) {
    if (value == null || value === '') return '';
    if (typeof value === 'string') {
        const parsed = parseDDMMYYYY(value.trim());
        if (parsed) return formatDDMMYYYY(parsed);
    }
    const d = coerceToDate(value);
    if (d) return formatDDMMYYYY(d);
    return typeof value === 'string' ? value.trim() : '';
}

/**
 * Value for HTML input[type=date] from mixed order date fields (ISO, DD-MM-YYYY, Timestamp).
 * @param {*} value
 * @returns {string} YYYY-MM-DD or empty string
 */
export function toDateInputValue(value) {
    if (value == null || value === '') return '';
    if (typeof value === 'string') {
        const parsed = parseDDMMYYYY(value.trim());
        if (parsed) return formatYYYYMMDD(parsed);
    }
    const d = coerceToDate(value);
    return d ? formatYYYYMMDD(d) : '';
}

/**
 * When the customer placed the order (orderDate preferred, then date_added).
 * @param {object} order - Firestore order document fields
 * @returns {Date|null}
 */
export function resolveOrderPlacedDate(order) {
    if (!order || typeof order !== 'object') return null;
    return (
        coerceToDate(order.orderDate) ||
        coerceToDate(order.date_added) ||
        coerceToDate(order.customer?.date_added) ||
        null
    );
}

/**
 * Human-readable date + time for when the customer placed the order.
 * @param {object} order - Firestore order document fields
 * @param {string} [locale='en-GB']
 * @returns {string}
 */
export function formatOrderPlacedDateTime(order, locale = 'en-GB') {
    const d = resolveOrderPlacedDate(order);
    if (!d) return '—';
    return new Intl.DateTimeFormat(locale, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).format(d);
}

// ============================================================================
// WEEK OPERATIONS
// ============================================================================

/**
 * Get start of week (Monday) for a given date
 * @param {string} dateStr - Date string to get week start for
 * @returns {string|null} Week start in YYYY-MM-DD format or null if invalid
 */
export function getStartOfWeek(dateStr) {
    const date = parseDDMMYYYY(dateStr) || new Date(dateStr);
    if (!date || isNaN(date.getTime())) return null;
    
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Monday start
    const monday = new Date(date.setDate(diff));
    monday.setHours(0, 0, 0, 0);
    
    return formatYYYYMMDD(monday);
}

/**
 * Get week range (Monday to Sunday) for a given week start
 * @param {string} weekStart - Week start date string
 * @returns {object} Object with start and end date strings in YYYY-MM-DD format
 */
export function getWeekRange(weekStart) {
    const monday = parseDDMMYYYY(weekStart) || new Date(weekStart);
    if (!monday || isNaN(monday.getTime())) return { start: '', end: '' };
    
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    
    return {
        start: formatYYYYMMDD(monday),
        end: formatYYYYMMDD(sunday)
    };
}

// ============================================================================
// VALIDATION AND COMPARISON
// ============================================================================

/**
 * Check if a date string is valid
 * @param {string} dateStr - Date string to validate
 * @returns {boolean} True if valid date, false otherwise
 */
export function isValidDate(dateStr) {
    if (!dateStr) return false;
    const date = parseDDMMYYYY(dateStr);
    return date !== null && !isNaN(date.getTime());
}

/**
 * Compare two date strings (returns -1, 0, or 1)
 * @param {string} dateStr1 - First date string
 * @param {string} dateStr2 - Second date string
 * @returns {number} -1 if date1 < date2, 0 if equal, 1 if date1 > date2
 */
export function compareDates(dateStr1, dateStr2) {
    const date1 = parseDDMMYYYY(dateStr1);
    const date2 = parseDDMMYYYY(dateStr2);
    
    if (!date1 || !date2) return 0;
    
    if (date1 < date2) return -1;
    if (date1 > date2) return 1;
    return 0;
}

// ============================================================================
// DATE RANGE UTILITIES
// ============================================================================

/**
 * Get manager report date range: month-to-date or past Monday–Sunday week.
 * @param {'month-to-date'|'past-week'} focus - Report focus
 * @param {string} [upToDate] - For month-to-date: end date (YYYY-MM-DD). For past-week: any date in the week (YYYY-MM-DD), or omit for last week
 * @returns {{ startDate: string, endDate: string }} startDate and endDate in YYYY-MM-DD
 */
export function getManagerReportDateRange(focus, upToDate) {
    const refDate = upToDate ? (parseDDMMYYYY(upToDate) || new Date(upToDate)) : new Date();
    if (!refDate || isNaN(refDate.getTime())) {
        const t = today();
        return { startDate: t, endDate: t };
    }
    const refStr = formatYYYYMMDD(refDate);
    if (focus === 'month-to-date') {
        const y = refDate.getFullYear();
        const m = String(refDate.getMonth() + 1).padStart(2, '0');
        return { startDate: `${y}-${m}-01`, endDate: refStr };
    }
    // past-week: Monday–Sunday containing refDate, or "last week" if no upToDate
    let weekStart;
    if (upToDate) {
        weekStart = getStartOfWeek(refStr);
    } else {
        // Last week: go back 7 days then get that week's Monday
        const lastWeekDate = new Date(refDate);
        lastWeekDate.setDate(lastWeekDate.getDate() - 7);
        weekStart = getStartOfWeek(formatYYYYMMDD(lastWeekDate));
    }
    if (!weekStart) return { startDate: refStr, endDate: refStr };
    const { start, end } = getWeekRange(weekStart);
    return { startDate: start, endDate: end };
}

/**
 * Get date range filter options
 * @returns {Array} Array of date range options
 */
export function getDateRangeOptions() {
    return [
        { value: 'all', label: 'All Time' },
        { value: '7', label: 'Last 7 Days' },
        { value: '30', label: 'Last 30 Days' },
        { value: '90', label: 'Last 90 Days' }
    ];
}

/**
 * Filter dates by range
 * @param {Array} dates - Array of date strings
 * @param {string|number} rangeDays - Number of days or 'all'
 * @returns {Array} Filtered array of date strings
 */
export function filterDatesByRange(dates, rangeDays) {
    if (rangeDays === 'all') return dates;
    
    const now = new Date();
    const cutoffDate = new Date(now.getTime() - (parseInt(rangeDays) * 24 * 60 * 60 * 1000));
    
    return dates.filter(dateStr => {
        const date = parseDDMMYYYY(dateStr);
        return date && date >= cutoffDate;
    });
}

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default {
    // Parsing
    parseDDMMYYYY,
    
    // Formatting
    formatDDMMYYYY,
    formatYYYYMMDD,
    formatReadableDate,
    today,
    
    // Firestore Timestamp operations
    dateStringToTimestamp,
    timestampToDateString,
    timestampToLocaleString,
    coerceToDate,
    resolveOrderPlacedDate,
    formatOrderPlacedDateTime,
    
    // Week operations
    getStartOfWeek,
    getWeekRange,
    
    // Validation and comparison
    isValidDate,
    compareDates,
    
    // Date range utilities
    getDateRangeOptions,
    filterDatesByRange,
    getManagerReportDateRange
};

